/**
 * Import Step 4: Complete Component
 * 
 * Fourth step of the import process - shows import completion status
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { hybridAccountService } from '../../services/hybrid-account-service';
import { transactionService } from '../../services/transaction-service';
import { transactionKeys } from '../../hooks/useTransactions';
import { getCurrentUserId } from '../../lib/supabase';
import { fetchTransactionIdsForImportSession } from '../../utils/import-session-transaction-ids';
import type { Account } from '../../types/account.types';

interface ImportStep4CompleteProps {
  fileName: string;
  accountId: string;
  imported: number;
  failed: number;
  errors: string[];
  warnings?: string[];
  importSessionId?: string;
  totalRows?: number;
  skippedDuplicates?: number;
  skippedZeroAmounts?: number;
  onClose: () => void;
}

export function ImportStep4Complete({
  fileName,
  accountId,
  imported,
  failed,
  errors,
  warnings = [],
  importSessionId,
  totalRows,
  skippedDuplicates = 0,
  skippedZeroAmounts = 0,
  onClose
}: ImportStep4CompleteProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [account, setAccount] = useState<Account | null>(null);
  const [categorizing, setCategorizing] = useState(false);
  const [categorizationResult, setCategorizationResult] = useState<{ matched: number; unmatched: number; failed: number; errors: string[] } | null>(null);
  const [categorizationSkipped, setCategorizationSkipped] = useState(false);

  // Fetch account details
  useEffect(() => {
    const fetchAccount = async () => {
      if (accountId) {
        try {
          const result = await hybridAccountService.getAccountById(accountId);
          if (result.data) {
            setAccount(result.data);
          }
        } catch (error) {
          console.error('Failed to fetch account:', error);
        }
      }
    };
    fetchAccount();
  }, [accountId]);

  const isSuccess = failed === 0;

  // Handle auto-categorization
  const handleAutoCategorize = async () => {
    if (!importSessionId) {
      return;
    }

    setCategorizing(true);
    setCategorizationResult(null);

    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        setCategorizationResult({
          matched: 0,
          unmatched: 0,
          failed: 0,
          errors: ['User not authenticated']
        });
        setCategorizing(false);
        return;
      }

      // Get imported transaction IDs
      const transactionIds = await fetchTransactionIdsForImportSession(
        importSessionId,
        accountId,
        imported,
        userId
      );

      if (transactionIds.length === 0) {
        setCategorizationResult({
          matched: 0,
          unmatched: 0,
          failed: 0,
          errors: ['No imported transactions found']
        });
        setCategorizing(false);
        return;
      }

      // Call bulk categorization service
      const result = await transactionService.bulkCategoriseByDescription(transactionIds, userId);

      if (result.error) {
        setCategorizationResult({
          matched: 0,
          unmatched: 0,
          failed: transactionIds.length,
          errors: [result.error.message]
        });
      } else if (result.data) {
        setCategorizationResult(result.data);
        // Invalidate status counts query to update the uncategorised badge count
        await queryClient.invalidateQueries({ queryKey: transactionKeys.statusCounts });
        // Also invalidate transaction lists since categories have changed
        await queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      } else {
        setCategorizationResult({
          matched: 0,
          unmatched: 0,
          failed: transactionIds.length,
          errors: ['Unknown error occurred']
        });
      }
    } catch (error: any) {
      setCategorizationResult({
        matched: 0,
        unmatched: 0,
        failed: 0,
        errors: [error.message || 'Failed to categorize transactions']
      });
    } finally {
      setCategorizing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Result */}
      <div className={`border rounded-lg p-6 ${
        isSuccess
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      }`}>
        <div className="flex items-start space-x-3">
          {isSuccess ? (
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h4 className={`text-lg font-semibold mb-3 ${
              isSuccess
                ? 'text-green-800 dark:text-green-200'
                : 'text-red-800 dark:text-red-200'
            }`}>
              {isSuccess ? 'Import Complete' : 'Import Failed'}
            </h4>
            {isSuccess && (
              <div className="space-y-3">
                <div className="text-base">
                  {fileName && (
                    <p className={`${
                      'text-green-700 dark:text-green-300'
                    }`}>
                      <span className="font-medium">File imported:</span> {fileName}
                    </p>
                  )}
                  {account && (
                    <p className={`mt-1 ${
                      'text-green-700 dark:text-green-300'
                    }`}>
                      <span className="font-medium">Account imported into:</span> {account.name} {account.accountNumber ? `XXXXX${account.accountNumber.slice(-4)}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-base">
                  <p className={`font-medium ${
                    'text-green-800 dark:text-green-200'
                  }`}>
                    Transactions imported: {imported} {totalRows !== undefined ? `of ${totalRows}` : ''} transaction{totalRows !== undefined && totalRows !== 1 ? 's' : imported !== 1 ? 's' : ''}
                  </p>
                </div>
                {(skippedDuplicates > 0 || skippedZeroAmounts > 0) && (
                  <div className="pt-3 border-t border-green-200 dark:border-green-700">
                    <p className={`text-base font-medium mb-2 ${
                      'text-green-800 dark:text-green-200'
                    }`}>
                      Skipped records:
                    </p>
                    <div className={`text-base space-y-1 ${
                      'text-green-700 dark:text-green-300'
                    }`}>
                      {skippedDuplicates > 0 && (
                        <p>• Duplicate records: {skippedDuplicates} {totalRows !== undefined ? `out of ${totalRows}` : ''}</p>
                      )}
                      {skippedZeroAmounts > 0 && (
                        <p>• Zero amount records: {skippedZeroAmounts} {totalRows !== undefined ? `out of ${totalRows}` : ''}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {!isSuccess && (
              <div className="space-y-3">
                <div className="text-sm">
                  {fileName && (
                    <p className={`${
                      'text-red-700 dark:text-red-300'
                    }`}>
                      <span className="font-medium">File imported:</span> {fileName}
                    </p>
                  )}
                  {account && (
                    <p className={`mt-1 ${
                      'text-red-700 dark:text-red-300'
                    }`}>
                      <span className="font-medium">Account imported into:</span> {account.name} {account.accountNumber ? `XXXXX${account.accountNumber.slice(-4)}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-sm">
                  <p className={`font-medium ${
                    'text-red-800 dark:text-red-200'
                  }`}>
                    Imported: {imported} {totalRows !== undefined ? `of ${totalRows}` : ''} transaction{totalRows !== undefined && totalRows !== 1 ? 's' : imported !== 1 ? 's' : ''} | Failed: {failed}
                  </p>
                </div>
                {(skippedDuplicates > 0 || skippedZeroAmounts > 0) && (
                  <div className="pt-3 border-t border-red-200 dark:border-red-700">
                    <p className={`text-xs font-medium mb-2 ${
                      'text-red-800 dark:text-red-200'
                    }`}>
                      Skipped records:
                    </p>
                    <div className={`text-sm space-y-1 ${
                      'text-red-700 dark:text-red-300'
                    }`}>
                      {skippedDuplicates > 0 && (
                        <p>• Duplicate records: {skippedDuplicates} {totalRows !== undefined ? `out of ${totalRows}` : ''}</p>
                      )}
                      {skippedZeroAmounts > 0 && (
                        <p>• Zero amount records: {skippedZeroAmounts} {totalRows !== undefined ? `out of ${totalRows}` : ''}</p>
                      )}
                    </div>
                  </div>
                )}
                {errors.length > 0 && (
                  <div className="pt-3 border-t border-red-200 dark:border-red-700">
                    <p className={`text-xs font-medium mb-2 ${
                      'text-red-800 dark:text-red-200'
                    }`}>
                      Error Details:
                    </p>
                    <ul className={`text-sm space-y-1 max-h-48 overflow-y-auto ${
                      'text-red-700 dark:text-red-300'
                    }`}>
                      {errors.map((error, index) => (
                        <li key={index} className="break-words">• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                Warnings ({warnings.length})
              </h4>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 max-h-48 overflow-y-auto">
                {warnings.slice(0, 10).map((warning, index) => (
                  <li key={index} className="break-words">• {warning}</li>
                ))}
                {warnings.length > 10 && (
                  <li>... and {warnings.length - 10} more warnings</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Auto-categorization Section - Always show if transactions imported and not skipped */}
      {isSuccess && imported > 0 && !categorizationSkipped && !categorizationResult && importSessionId && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
            Auto-categorize Transactions
          </h4>
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
            Automatically categorize imported transactions based on matching descriptions from your previously categorized transactions. This feature improves over time as you categorize more transactions.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setCategorizationSkipped(true)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium transition-colors"
            >
              Skip
            </button>
            <button
              onClick={handleAutoCategorize}
              disabled={categorizing}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {categorizing && <Loader2 className="h-4 w-4 animate-spin" />}
              {categorizing ? 'Categorizing...' : 'Auto-categorize'}
            </button>
          </div>
        </div>
      )}

      {/* Categorization Results */}
      {categorizationResult && (
        <div className={`border rounded-lg p-4 ${
          categorizationResult.failed === 0 && categorizationResult.errors.length === 0
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-start space-x-3">
            {categorizationResult.failed === 0 && categorizationResult.errors.length === 0 ? (
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h4 className={`font-medium mb-2 ${
                categorizationResult.failed === 0 && categorizationResult.errors.length === 0
                  ? 'text-green-800 dark:text-green-200'
                  : 'text-yellow-800 dark:text-yellow-200'
              }`}>
                Auto-categorization Complete
              </h4>
              <div className={`text-sm space-y-1 ${
                categorizationResult.failed === 0 && categorizationResult.errors.length === 0
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-yellow-700 dark:text-yellow-300'
              }`}>
                <p>• Categorized: {categorizationResult.matched} transactions</p>
                {categorizationResult.unmatched > 0 && (
                  <p>• Unmatched: {categorizationResult.unmatched} transactions</p>
                )}
                {categorizationResult.failed > 0 && (
                  <p>• Failed: {categorizationResult.failed} transactions</p>
                )}
              </div>
              {categorizationResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className={`text-sm font-medium mb-2 ${
                    categorizationResult.failed === 0 && categorizationResult.errors.length === 0
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-yellow-800 dark:text-yellow-200'
                  }`}>
                    Errors:
                  </p>
                  <ul className={`text-sm space-y-1 max-h-48 overflow-y-auto ${
                    categorizationResult.failed === 0 && categorizationResult.errors.length === 0
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-yellow-700 dark:text-yellow-300'
                  }`}>
                    {categorizationResult.errors.map((error, index) => (
                      <li key={index} className="break-words">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Close Button */}
      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

